// Shared, tenant-agnostic renderer for an autowatch run report (report.json).
//
// A React/MUI port of the report.html client renderer (renderClient in the claudecode
// runner's lib/report.js): meta line, stat tiles, confident violations, the grey section,
// an examined-clean fold, and the cost table. Purely presentational — it takes a parsed
// report.json object and renders it; fetching/format-detection live in RunReport.jsx so
// both the appesteem (LogViewer) and inspect (VisualStory) tenants can reuse this view.

import React from 'react';
import { Box, Typography, Chip } from '@mui/material';

/**
 * Return the report.json object from whatever AutowatchReportUrl pointed at:
 *   - report.json  → the fetched text IS the JSON (runs produced by the current runner).
 *   - report.html  → the SAME report.json is inlined under <script id="report-data"> (runs
 *                    produced before the runner started writing report.json to the field).
 * Returns null when it's neither (e.g. a legacy report.md) so the caller renders markdown.
 */
export function extractReportJson(text) {
  if (!text) return null;
  // report.json: the text is the JSON itself.
  if (text.trimStart().startsWith('{')) {
    try {
      const o = JSON.parse(text);
      if (o && typeof o === 'object' && o.summary) return o;
    } catch { /* not JSON — try the embedded form below */ }
  }
  // report.html: the same JSON inlined in a script tag (`<` is escaped as <,
  // which JSON.parse decodes natively, so no manual unescape is needed).
  const m = text.match(/<script id="report-data" type="application\/json">([\s\S]*?)<\/script>/);
  if (m) {
    try {
      const o = JSON.parse(m[1]);
      if (o && typeof o === 'object' && o.summary) return o;
    } catch { /* malformed embed — treat as no report */ }
  }
  return null; // neither → caller falls back to markdown
}

const REPORT_ACCENT = '#d63031';
const reportH2Sx = { fontSize: 17, mt: 3.5, mb: 1.25, borderBottom: '1px solid #dfe4ea', pb: 0.75, fontWeight: 600 };
const reportH3GreySx = { color: '#8a97a5', mt: 2, mb: 0.75, fontSize: 15, fontWeight: 600 };
const quoteSx = { my: 0.75, py: 0.75, px: 1.25, borderLeft: '3px solid #c9d2dc', background: '#f2f5f8', fontSize: 12, color: '#46586a' };

function ReportStat({ tone, value, label }) {
  const color = tone === 'bad' ? 'error.main' : tone === 'warn' ? 'warning.main' : tone === 'ok' ? 'success.main' : 'text.primary';
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1.75, py: 1, minWidth: 90 }}>
      <Typography sx={{ fontSize: 18, fontWeight: 700, color, lineHeight: 1.2 }}>{String(value)}</Typography>
      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{label}</Typography>
    </Box>
  );
}

function ReportEvidence({ ev }) {
  const isVisual = ev.kind === 'screenshot' || ev.kind === 'doc';
  if (isVisual && ev.url) {
    return (
      <Box component="figure" sx={{ m: 0, width: 220 }}>
        <a href={ev.url} target="_blank" rel="noopener noreferrer">
          <Box component="img" loading="lazy" src={ev.url}
               sx={{ width: '100%', border: '1px solid', borderColor: 'divider', borderRadius: 1, background: '#eee', display: 'block' }} />
        </a>
        <Typography component="figcaption" sx={{ fontSize: 11, color: 'text.secondary', textAlign: 'center', mt: 0.25 }}>
          <a href={ev.url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'none' }}>open ↗</a>
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Chip size="small" color={ev.resolved ? 'default' : 'warning'}
            label={isVisual ? `${ev.ref} (${ev.resolved ? 'resolved — not renderable here' : 'unresolved'})` : ev.ref}
            sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 11 }} />
      {ev.text && (
        <Box component="details" sx={{ fontSize: 12 }}>
          <Box component="summary" sx={{ cursor: 'pointer', color: 'text.secondary' }}>full text</Box>
          <Box component="pre" sx={{ whiteSpace: 'pre-wrap', fontSize: 12, color: 'text.secondary', m: 0 }}>{ev.text}</Box>
        </Box>
      )}
    </Box>
  );
}

function ReportEmitCard({ emit }) {
  return (
    <Box sx={{ borderTop: '1px dashed', borderColor: 'divider', pt: 1.25, mt: 1.25,
               ...(emit.shippable ? {} : { background: '#fff9ec', borderRadius: 1, p: 1.25 }) }}>
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 0.75, alignItems: 'center' }}>
        <Chip size="small" label={emit.phase} sx={{ background: '#1c2733', color: '#fff', height: 20, fontSize: 11 }} />
        {emit.recipe && <Chip size="small" label={emit.recipe} sx={{ height: 20, fontSize: 11 }} />}
        {!emit.shippable && <Chip size="small" color="warning" label={`unresolved evidence: ${(emit.unresolvedEvidence || []).join(', ')}`} sx={{ height: 20, fontSize: 11 }} />}
      </Box>
      <Typography sx={{ fontSize: 14, my: 0.5 }}>{emit.explanation}</Typography>
      {emit.catalogQuote && <Box component="blockquote" sx={quoteSx}>rule: “{emit.catalogQuote}”</Box>}
      {emit.anchorKind === 'ux' && emit.uxContext && <Box component="blockquote" sx={{ ...quoteSx, borderLeftColor: '#1f9d55' }}>on-screen: “{emit.uxContext}”</Box>}
      {emit.anchorKind === 'event' && emit.eventRef && <Chip size="small" label={`event: ${emit.eventRef}`} sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 11 }} />}
      {(emit.evidence || []).length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, mt: 1 }}>
          {emit.evidence.map((ev, i) => <ReportEvidence key={i} ev={ev} />)}
        </Box>
      )}
    </Box>
  );
}

function ReportViolationSection({ v, grey }) {
  return (
    <Box component="section" sx={{ border: '1px solid', borderColor: 'divider', borderLeft: '4px solid',
         borderLeftColor: grey ? '#aab4bf' : REPORT_ACCENT, background: grey ? '#fafbfc' : 'background.paper',
         borderRadius: 1, p: '14px 16px', my: 1.75 }}>
      <Typography component="h3" sx={{ m: 0, mb: 0.75, fontSize: 15, fontWeight: 600 }}>
        {v.acrId}
        <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary', fontSize: 12, ml: 0.75 }}>
          {v.emits.length} finding{v.emits.length === 1 ? '' : 's'}
        </Box>
      </Typography>
      <Typography sx={{ fontSize: 13, color: '#3d4d5d', mt: 0.5, mb: 1 }}>{v.rule || '(rule text unavailable)'}</Typography>
      {v.ruleDetails && (
        <Box component="details" sx={{ mb: 1 }}>
          <Box component="summary" sx={{ cursor: 'pointer', fontSize: 12, color: 'text.secondary' }}>full rule details</Box>
          <Typography sx={{ fontSize: 12, color: '#5a6a7a' }}>{v.ruleDetails}</Typography>
        </Box>
      )}
      {v.emits.map((e, i) => <ReportEmitCard key={i} emit={e} />)}
    </Box>
  );
}

function ReportDispoList({ items, grey }) {
  return (
    <Box component="ul" sx={{ fontSize: 13, color: grey ? '#8a97a5' : '#5a6a7a', pl: 2.25, my: 0.75 }}>
      {items.map((item, i) => (
        <Box component="li" key={i} sx={{ my: 0.5 }}>
          <b>{item.acrId}</b>{' — '}{item.rule || '(rule text unavailable)'}
          {item.reason && <Box component="span" sx={{ color: 'text.secondary' }}>{' · '}{item.reason}</Box>}
        </Box>
      ))}
    </Box>
  );
}

function ReportCostTable({ cost }) {
  if (!cost) return <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>cost-breakdown.json not found.</Typography>;
  const cl = cost.claude || {};
  const cu = cost.geminiCu || {};
  const cellSx = { p: '7px 12px', borderBottom: '1px solid #eef2f6', textAlign: 'left' };
  const numSx = { ...cellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
  const totalBorder = '2px solid #1c2733';
  const rows = [];
  if (cl.byRole) {
    Object.keys(cl.byRole).forEach((role) => {
      const v = cl.byRole[role];
      rows.push({
        comp: `Claude — ${role}`,
        detail: `${v.sessions} session(s), ${v.turns} turn(s)${v.timedOut ? `, ${v.timedOut} timed out` : ''}${v.mcpFailed ? `, ${v.mcpFailed} MCP-failed` : ''}`,
        usd: `$${v.costUsd}`,
      });
    });
  } else {
    rows.push({ comp: 'Claude (driver + grader)', detail: `${cl.turns || 0} turns (legacy totals)`, usd: `$${cl.costUsd}` });
  }
  rows.push({ comp: 'Gemini Computer-Use', detail: `${cu.calls || 0} call(s), Vertex list price (metered)`, usd: `$${cu.costUsd}` });
  return (
    <>
      <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%', border: '1px solid', borderColor: 'divider', borderRadius: 1, fontSize: 13 }}>
        <Box component="thead">
          <Box component="tr">
            <Box component="th" sx={cellSx}>Component</Box>
            <Box component="th" sx={cellSx}>Detail</Box>
            <Box component="th" sx={numSx}>USD</Box>
          </Box>
        </Box>
        <Box component="tbody">
          {rows.map((r, i) => (
            <Box component="tr" key={i}>
              <Box component="td" sx={cellSx}>{r.comp}</Box>
              <Box component="td" sx={cellSx}>{r.detail}</Box>
              <Box component="td" sx={numSx}>{r.usd}</Box>
            </Box>
          ))}
          <Box component="tr">
            <Box component="td" sx={{ ...cellSx, fontWeight: 700, borderTop: totalBorder }}>Total</Box>
            <Box component="td" sx={{ ...cellSx, borderTop: totalBorder }} />
            <Box component="td" sx={{ ...numSx, fontWeight: 700, borderTop: totalBorder }}>${cost.totalUsd}</Box>
          </Box>
        </Box>
      </Box>
      {(cl.billing || cl.source) && (
        <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>
          {cl.billing || ''}{cl.source ? ` · source: ${cl.source}` : ''}
        </Typography>
      )}
    </>
  );
}

export default function AutowatchReportView({ data }) {
  const s = data.summary || {};
  const stats = [
    ['bad', s.failingAcrCount, 'confident violations'],
    ['bad', s.shippableEmitCount, 'shippable findings'],
    ['warn', s.flaggedEmitCount, 'flagged (unresolved evidence)'],
    ['warn', s.undecidableCount, 'undecidable'],
    ['ok', s.cleanCount, 'examined clean'],
    [s.unaccountedCount ? 'warn' : 'ok', s.unaccountedCount == null ? '?' : s.unaccountedCount, 'unaccounted'],
    ['', s.totalUsd == null ? '?' : `$${s.totalUsd}`, 'total cost'],
  ];
  const confident = data.confident || [];
  const flagged = data.flagged || [];
  const undecidable = data.undecidable || [];
  const unaccounted = data.unaccounted || [];
  const clean = data.clean || [];
  const flaggedTotal = flagged.reduce((n, v) => n + v.emits.length, 0);
  const greyEmpty = !(flagged.length || undecidable.length || unaccounted.length);
  return (
    <Box>
      <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1.25, borderBottom: `2px solid ${REPORT_ACCENT}`, pb: 1 }}>
        run {data.run} · outcome: {data.outcome?.kind} (final phase: {data.outcome?.finalPhase || '?'}) · generated {data.ts}
        {data.graded ? '' : ' · NOT GRADED — findings below are incomplete'}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {stats.map((t, i) => <ReportStat key={i} tone={t[0]} value={t[1]} label={t[2]} />)}
      </Box>

      <Typography component="h2" sx={reportH2Sx}>
        Confident violations{' '}
        <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary', fontSize: 12 }}>
          a violation was emitted and its cited evidence resolved on disk
        </Box>
      </Typography>
      {confident.length
        ? confident.map((v, i) => <ReportViolationSection key={i} v={v} />)
        : <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>No confident violations.</Typography>}

      <Typography component="h2" sx={{ ...reportH2Sx, color: '#8a97a5' }}>
        Grey — examined, not confidently confirmed
      </Typography>
      {greyEmpty && (
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
          Nothing unresolved — every examined rule was decided one way or the other.
        </Typography>
      )}
      {flaggedTotal > 0 && (
        <>
          <Typography component="h3" sx={reportH3GreySx}>
            Flagged findings ({flaggedTotal} — a violation was asserted but its evidence did not resolve on disk; NOT shipped, kept visible)
          </Typography>
          {flagged.map((v, i) => <ReportViolationSection key={i} v={v} grey />)}
        </>
      )}
      {undecidable.length > 0 && (
        <>
          <Typography component="h3" sx={reportH3GreySx}>
            Undecidable ({undecidable.length} — the deciding surface was never captured)
          </Typography>
          <ReportDispoList items={undecidable} grey />
        </>
      )}
      {unaccounted.length > 0 && (
        <>
          <Typography component="h3" sx={reportH3GreySx}>
            Not examined — open holes ({unaccounted.length}; the grade is incomplete)
          </Typography>
          <ReportDispoList items={unaccounted} grey />
        </>
      )}

      {clean.length > 0 && (
        <Box component="details" sx={{ my: 1.25 }}>
          <Box component="summary" sx={{ cursor: 'pointer', fontSize: 13, color: '#3d4d5d' }}>
            Examined — no violation ({clean.length} rule{clean.length === 1 ? '' : 's'} confidently cleared)
          </Box>
          <ReportDispoList items={clean} />
        </Box>
      )}

      <Typography component="h2" sx={reportH2Sx}>Cost</Typography>
      <ReportCostTable cost={data.cost} />
    </Box>
  );
}
